import { Shield } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Task } from '@/services/types/client-intake-types';
import { CanonicalTaskGroup } from '@/services/types/task-types';

import { Day1TaskGroups } from './Day1TaskGroups';

interface MatterWithTasks {
  id: string;
  name: string;
  status?: string;
  type: string;
  city?: string;
  state?: string;
  tasks: Task[];
  completedTasksCount: number;
  pendingTasksCount: number;
  urgentTasksCount: number;
  isDay1Matter: boolean;
  day1QualifyingGroups: CanonicalTaskGroup[];
  isNewMatter: boolean;
}

interface FirmGroup {
  firm: { id: string; name: string };
  matters: MatterWithTasks[];
  totalMatters: number;
  urgentCount: number;
  completedCount: number;
  pendingCount: number;
}

interface Day1FirmGroupProps {
  firmGroup: FirmGroup;
  onTaskClick: (taskId: string) => void;
  isDay1Task?: (task: Task) => boolean;
}

export const Day1FirmGroup: React.FC<Day1FirmGroupProps> = ({
  firmGroup,
  onTaskClick,
  isDay1Task,
}) => {
  const navigate = useNavigate();

  return (
    <Card className="overflow-hidden">
      <div className="p-6 bg-gray-50 border-b">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {firmGroup.firm.name}
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {firmGroup.totalMatters}{' '}
              {firmGroup.totalMatters === 1 ? 'matter' : 'matters'} •{' '}
              {firmGroup.completedCount} completed • {firmGroup.pendingCount}{' '}
              pending
              {firmGroup.urgentCount > 0 && (
                <span className="text-red-600 font-medium">
                  {' '}
                  • {firmGroup.urgentCount} urgent
                </span>
              )}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/matters?firm=${firmGroup.firm.id}`)}
          >
            View All Matters
          </Button>
        </div>
      </div>

      <div className="divide-y">
        {firmGroup.matters.map((matter) => (
          <div key={matter.id} className="p-6">
            <div className="mb-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="text-base font-medium text-blue-600 hover:text-blue-800 cursor-pointer text-left"
                    onClick={() => navigate(`/matters/${matter.id}`)}
                  >
                    {matter.name}
                  </button>
                  {/* Day 1 Badge */}
                  {matter.isDay1Matter && (
                    <div className="flex items-center gap-1">
                      <Shield
                        className="h-4 w-4 text-yellow-600"
                        fill="currentColor"
                      />
                      {matter.isNewMatter && (
                        <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded font-medium">
                          New
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
                    {matter.status || 'Unknown'}
                  </span>
                  <Button
                    onClick={() => navigate(`/matters/${matter.id}`)}
                    variant="outline"
                    size="sm"
                  >
                    View Details
                  </Button>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                {matter.type} •{' '}
                {matter.city && matter.state
                  ? `${matter.city}, ${matter.state}`
                  : 'Location not specified'}
              </p>
              {/* Show qualifying Day 1 groups */}
              {matter.isDay1Matter &&
                matter.day1QualifyingGroups.length > 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    Outstanding Day 1 tasks:{' '}
                    {matter.day1QualifyingGroups.join(', ')}
                  </p>
                )}
            </div>

            {/* Task Groups Component */}
            <Day1TaskGroups
              tasks={matter.tasks}
              onTaskClick={onTaskClick}
              isDay1Task={isDay1Task}
              qualifyingGroups={matter.day1QualifyingGroups}
            />
          </div>
        ))}
      </div>
    </Card>
  );
};
